import React, { useEffect, useState } from 'react';
import './TimeClock.css';

interface Punch { type: 'in' | 'out'; time: string; pacific?: string; }
interface Period { start: string; end: string; }
interface Entry { id: string; kind: 'miles' | 'expense'; date: string; miles?: number; purpose?: string; items?: string; vendor?: string; amount?: number; receipt?: { ext: string; size: number }; }

const todayPacific = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
interface ClockState {
    clockedIn: boolean; sessionMs: number; todayMs: number; periodMs: number; recent: Punch[];
    admin?: boolean; employees?: { email: string; name: string }[]; entries?: Entry[];
    periods?: { current: Period; previous: Period };
}

const MAX_RECEIPT_BYTES = 3000000;

const readAsDataUrl = (file: Blob) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Unable to read the file.'));
    reader.readAsDataURL(file);
});

// Photos from phones are often larger than the upload limit, so images are shrunk in the browser first
async function prepareReceipt(file: File): Promise<{ name: string; data: string }> {
    let blob: Blob = file;
    if (file.type.startsWith('image/')) {
        const bitmap = await createImageBitmap(file);
        const scale = Math.min(1, 2000 / Math.max(bitmap.width, bitmap.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(bitmap.width * scale);
        canvas.height = Math.round(bitmap.height * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Unable to process the image.');
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        const out = await new Promise<Blob | null>(r => canvas.toBlob(r, 'image/jpeg', 0.85));
        if (!out) throw new Error('Unable to process the image.');
        blob = out;
    } else if (file.type !== 'application/pdf') {
        throw new Error('Receipts must be an image or a PDF file.');
    }
    if (blob.size > MAX_RECEIPT_BYTES) throw new Error('The receipt file is too large (3 MB maximum).');
    const url = await readAsDataUrl(blob);
    return { name: file.name, data: url.slice(url.indexOf(',') + 1) };
}

const toPacific = (p: Punch) => p.pacific || new Date(p.time).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', timeZoneName: 'short' });

const formatDuration = (ms: number) => {
    const totalMinutes = Math.floor(ms / 60000);
    return `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`;
};

const TOKEN_KEY = 'timeclock-token';
const NAME_KEY = 'timeclock-name';

async function callApi(action: string, extra: Record<string, unknown> = {}, token?: string) {
    const res = await fetch('/api/timeclock', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ action, ...extra }),
    });
    const data = await res.json().catch(() => ({}));
    return { status: res.status, data };
}

type Mode = 'login' | 'forgot' | 'reset';

function TimeClock() {
    const [token, setToken] = useState<string | null>(sessionStorage.getItem(TOKEN_KEY));
    const [name, setName] = useState(sessionStorage.getItem(NAME_KEY) || '');
    const [state, setState] = useState<ClockState | null>(null);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [resetToken, setResetToken] = useState('');
    const [mode, setMode] = useState<Mode>('login');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [busy, setBusy] = useState(false);
    const [range, setRange] = useState('current');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [employee, setEmployee] = useState('self');
    const [reportMessage, setReportMessage] = useState('');
    const [reportError, setReportError] = useState('');
    const [entryDate, setEntryDate] = useState(todayPacific());
    const [miles, setMiles] = useState('');
    const [purpose, setPurpose] = useState('');
    const [items, setItems] = useState('');
    const [amount, setAmount] = useState('');
    const [vendor, setVendor] = useState('');
    const [receiptFile, setReceiptFile] = useState<File | null>(null);
    const [receiptKey, setReceiptKey] = useState(0);
    const [entryMessage, setEntryMessage] = useState('');
    const [entryError, setEntryError] = useState('');
    type PanelName = 'miles' | 'expense' | 'payroll' | 'edit';
    const [panel, setPanel] = useState<PanelName | null>(null);
    const [editEmployee, setEditEmployee] = useState('');
    const [editDate, setEditDate] = useState(todayPacific());
    const [editShifts, setEditShifts] = useState<{ in: string; out: string }[] | null>(null);
    const [editMessage, setEditMessage] = useState('');
    const [editError, setEditError] = useState('');
    const togglePanel = (p: PanelName) => {
        setPanel(prev => (prev === p ? null : p));
        setEntryMessage(''); setEntryError(''); setReportMessage(''); setReportError('');
        setEditMessage(''); setEditError(''); setEditShifts(null);
    };

    const loadDay = async () => {
        if (!token) return;
        setBusy(true); setEditMessage(''); setEditError('');
        const { status, data } = await callApi('admin-get-day', { employee: editEmployee, date: editDate }, token);
        setBusy(false);
        if (status === 401) return logout(data.error);
        if (status === 200) setEditShifts(data.shifts);
        else { setEditShifts(null); setEditError(data.error || 'Request failed'); }
    };

    const saveDay = async () => {
        if (!token || !editShifts) return;
        setBusy(true); setEditMessage(''); setEditError('');
        const { status, data } = await callApi('admin-save-day', { employee: editEmployee, date: editDate, shifts: editShifts }, token);
        setBusy(false);
        if (status === 401) return logout(data.error);
        if (status === 200) {
            setEditShifts(data.shifts);
            setEditMessage(data.message);
            const refreshed = await callApi('status', {}, token);
            if (refreshed.status === 200) setState(refreshed.data);
        } else setEditError(data.error || 'Request failed');
    };

    const updateShift = (i: number, field: 'in' | 'out', value: string) =>
        setEditShifts(prev => (prev ? prev.map((s, j) => (j === i ? { ...s, [field]: value } : s)) : prev));

    useEffect(() => {
        const meta = document.createElement('meta');
        meta.name = 'robots';
        meta.content = 'noindex, nofollow';
        document.head.appendChild(meta);

        const params = new URLSearchParams(window.location.search);
        const t = params.get('reset');
        if (t) {
            setResetToken(t);
            setMode('reset');
            window.history.replaceState({}, '', window.location.pathname);
        }
        return () => { document.head.removeChild(meta); };
    }, []);

    const logout = (message = '') => {
        sessionStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem(NAME_KEY);
        setToken(null);
        setState(null);
        setError(message);
    };

    useEffect(() => {
        if (!token) return;
        callApi('status', {}, token).then(({ status, data }) => {
            if (status === 401) logout(data.error);
            else if (status === 200) setState(data);
            else setError(data.error || 'Unable to load status');
        });
    }, [token]);

    const switchMode = (m: Mode) => { setMode(m); setError(''); setNotice(''); setPassword(''); };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        setNotice('');
        const { status, data } = await callApi('login', { username: email.trim().toLowerCase(), password });
        setBusy(false);
        if (status !== 200) {
            setError(data.error || 'Login failed');
            return;
        }
        sessionStorage.setItem(TOKEN_KEY, data.token);
        sessionStorage.setItem(NAME_KEY, data.name);
        setName(data.name);
        setState(data);
        setToken(data.token);
        setPassword('');
    };

    const handleForgot = async (e: React.FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        const { status, data } = await callApi('request-reset', { email: email.trim().toLowerCase() });
        setBusy(false);
        if (status === 200) setNotice(data.message);
        else setError(data.error || 'Request failed');
    };

    const handleReset = async (e: React.FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        const { status, data } = await callApi('reset-password', { token: resetToken, newPassword: password });
        setBusy(false);
        if (status === 200) {
            setResetToken('');
            switchMode('login');
            setNotice(data.message);
        } else setError(data.error || 'Request failed');
    };

    const handlePunch = async () => {
        if (!state || !token) return;
        setBusy(true);
        setError('');
        const { status, data } = await callApi(state.clockedIn ? 'clock-out' : 'clock-in', {}, token);
        setBusy(false);
        if (status === 401) return logout(data.error);
        if (data.recent) setState(prev => ({ ...prev, ...data }));
        if (status !== 200) setError(data.error || 'Request failed');
    };

    const handlePayroll = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token) return;
        setBusy(true);
        setReportMessage('');
        setReportError('');
        const { status, data } = await callApi('send-payroll', { range, start: startDate, end: endDate, employee }, token);
        setBusy(false);
        if (status === 401) return logout(data.error);
        if (status === 200) setReportMessage(data.message + (data.warnings?.length ? ` Notes: ${data.warnings.join(' ')}` : ''));
        else setReportError(data.error || 'Request failed');
    };

    const handleEntry = (action: 'add-miles' | 'add-expense') => async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token) return;
        setBusy(true);
        setEntryMessage('');
        setEntryError('');
        const fields: Record<string, unknown> = action === 'add-miles' ? { miles, purpose } : { items, amount, vendor };
        if (action === 'add-expense' && receiptFile) {
            try { fields.receipt = await prepareReceipt(receiptFile); }
            catch (err) {
                setBusy(false);
                setEntryError(err instanceof Error ? err.message : 'Unable to read the receipt.');
                return;
            }
        }
        const { status, data } = await callApi(action, { date: entryDate, ...fields }, token);
        setBusy(false);
        if (status === 401) return logout(data.error);
        if (status === 200) {
            setEntryMessage(data.message);
            setState(prev => (prev ? { ...prev, entries: data.entries } : prev));
            if (action === 'add-miles') { setMiles(''); setPurpose(''); }
            else { setItems(''); setAmount(''); setVendor(''); setReceiptFile(null); setReceiptKey(k => k + 1); }
        } else setEntryError(data.error || 'Request failed');
    };

    const periodLabel = (p?: Period) => (p ? ` (${p.start} to ${p.end})` : '');

    return (
        <div className="timeclock">
            <div className="timeclock-card">
                <h1>Employee Time Clock</h1>
                {!token ? (
                    <>
                        {mode === 'login' && (
                            <form onSubmit={handleLogin}>
                                <input type="email" placeholder="Email" autoComplete="username" value={email}
                                    onChange={e => setEmail(e.target.value)} required />
                                <input type="password" placeholder="Password" autoComplete="current-password" value={password}
                                    onChange={e => setPassword(e.target.value)} required />
                                <button type="submit" disabled={busy}>{busy ? 'Signing in…' : 'Log In'}</button>
                                <button type="button" className="timeclock-link" onClick={() => switchMode('forgot')}>Forgot password?</button>
                            </form>
                        )}
                        {mode === 'forgot' && (
                            <form onSubmit={handleForgot}>
                                <p>Enter your email and we'll send you a link to reset your password.</p>
                                <input type="email" placeholder="Email" autoComplete="username" value={email}
                                    onChange={e => setEmail(e.target.value)} required />
                                <button type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send Reset Link'}</button>
                                <button type="button" className="timeclock-link" onClick={() => switchMode('login')}>Back to log in</button>
                            </form>
                        )}
                        {mode === 'reset' && (
                            <form onSubmit={handleReset}>
                                <p>Choose a new password (at least 10 characters).</p>
                                <input type="password" placeholder="New password" autoComplete="new-password" minLength={10}
                                    value={password} onChange={e => setPassword(e.target.value)} required />
                                <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Set Password'}</button>
                                <button type="button" className="timeclock-link" onClick={() => switchMode('login')}>Back to log in</button>
                            </form>
                        )}
                    </>
                ) : (
                    <>
                        <p>Signed in as <strong>{name}</strong></p>
                        {state && (
                            <>
                                <p className={state.clockedIn ? 'timeclock-in' : 'timeclock-out'}>
                                    {state.clockedIn ? 'You are clocked IN' : 'You are clocked OUT'}
                                </p>
                                <p className="timeclock-durations">
                                    {state.clockedIn && <>This session: <strong>{formatDuration(state.sessionMs)}</strong><br /></>}
                                    Today: <strong>{formatDuration(state.todayMs)}</strong><br />
                                    This pay period{state.periods ? ` (${state.periods.current.start} to ${state.periods.current.end})` : ''}: <strong>{formatDuration(state.periodMs)}</strong>
                                </p>
                                <button onClick={handlePunch} disabled={busy}>
                                    {state.clockedIn ? 'Clock Out' : 'Clock In'}
                                </button>
                                <button type="button" aria-expanded={panel === 'miles'} onClick={() => togglePanel('miles')}>Add mileage</button>
                                {panel === 'miles' && (
                                <form onSubmit={handleEntry('add-miles')} className="timeclock-report">
                                    <h3>Miles driven</h3>
                                    <label className="timeclock-field">Date <input type="date" value={entryDate} max={todayPacific()} onChange={e => setEntryDate(e.target.value)} required /></label>
                                    <input type="number" placeholder="Miles" min="0.1" max="2000" step="0.1" value={miles} onChange={e => setMiles(e.target.value)} required />
                                    <input type="text" placeholder="Purpose of travel" maxLength={200} value={purpose} onChange={e => setPurpose(e.target.value)} required />
                                    <button type="submit" disabled={busy}>Save mileage</button>
                                    {entryMessage && <p className="timeclock-notice" role="status">{entryMessage}</p>}
                                    {entryError && <p className="timeclock-error" role="alert">{entryError}</p>}
                                </form>
                                )}
                                <button type="button" aria-expanded={panel === 'expense'} onClick={() => togglePanel('expense')}>Add expense reimbursement</button>
                                {panel === 'expense' && (
                                <form onSubmit={handleEntry('add-expense')} className="timeclock-report">
                                    <h3>Expense reimbursement</h3>
                                    <label className="timeclock-field">Date <input type="date" value={entryDate} max={todayPacific()} onChange={e => setEntryDate(e.target.value)} required /></label>
                                    <input type="text" placeholder="Items purchased" maxLength={300} value={items} onChange={e => setItems(e.target.value)} required />
                                    <input type="number" placeholder="Total amount ($)" min="0.01" max="100000" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} required />
                                    <input type="text" placeholder="Purchased from" maxLength={120} value={vendor} onChange={e => setVendor(e.target.value)} required />
                                    <label>Receipt (optional) <input key={receiptKey} type="file" accept="image/*,application/pdf" onChange={e => setReceiptFile(e.target.files?.[0] || null)} /></label>
                                    <button type="submit" disabled={busy}>Save expense</button>
                                    {entryMessage && <p className="timeclock-notice" role="status">{entryMessage}</p>}
                                    {entryError && <p className="timeclock-error" role="alert">{entryError}</p>}
                                </form>
                                )}
                                <button type="button" aria-expanded={panel === 'payroll'} onClick={() => togglePanel('payroll')}>Request payroll report</button>
                                {panel === 'payroll' && (
                                <form onSubmit={handlePayroll} className="timeclock-report">
                                    <h3>Payroll report</h3>
                                    {state.admin && (
                                        <select value={employee} onChange={e => setEmployee(e.target.value)}>
                                            <option value="self">Myself</option>
                                            <option value="all">All employees</option>
                                            {state.employees?.map(emp => <option key={emp.email} value={emp.email}>{emp.name}</option>)}
                                        </select>
                                    )}
                                    <select value={range} onChange={e => setRange(e.target.value)}>
                                        <option value="current">Current pay period{periodLabel(state.periods?.current)}</option>
                                        <option value="previous">Previous pay period{periodLabel(state.periods?.previous)}</option>
                                        <option value="custom">Custom dates</option>
                                    </select>
                                    {range === 'custom' && (
                                        <>
                                            <label>Start <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} required /></label>
                                            <label>End <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} required /></label>
                                        </>
                                    )}
                                    <button type="submit" disabled={busy}>Email payroll report</button>
                                    {reportMessage && <p className="timeclock-notice" role="status">{reportMessage}</p>}
                                    {reportError && <p className="timeclock-error" role="alert">{reportError}</p>}
                                </form>
                                )}
                                {state.admin && (
                                    <>
                                        <button type="button" aria-expanded={panel === 'edit'} onClick={() => togglePanel('edit')}>Edit employee time records</button>
                                        {panel === 'edit' && (
                                            <div className="timeclock-report">
                                                <h3>Edit time records</h3>
                                                <select value={editEmployee} onChange={e => { setEditEmployee(e.target.value); setEditShifts(null); }}>
                                                    <option value="">Select employee</option>
                                                    {state.employees?.map(emp => <option key={emp.email} value={emp.email}>{emp.name}</option>)}
                                                </select>
                                                <label className="timeclock-field">Date <input type="date" value={editDate} max={todayPacific()} onChange={e => { setEditDate(e.target.value); setEditShifts(null); }} /></label>
                                                <button type="button" disabled={busy || !editEmployee || !editDate} onClick={loadDay}>Load day</button>
                                                {editShifts && (
                                                    <>
                                                        {editShifts.length === 0 && <p>No time entries on this day.</p>}
                                                        {editShifts.map((s, i) => (
                                                            <div key={i} className="timeclock-field">
                                                                <label>In <input type="time" value={s.in} onChange={e => updateShift(i, 'in', e.target.value)} required /></label>
                                                                <label>Out <input type="time" value={s.out} onChange={e => updateShift(i, 'out', e.target.value)} /></label>
                                                                <button type="button" className="timeclock-secondary" onClick={() => setEditShifts(prev => (prev ? prev.filter((_, j) => j !== i) : prev))}>Remove</button>
                                                            </div>
                                                        ))}
                                                        <button type="button" className="timeclock-secondary" onClick={() => setEditShifts(prev => [...(prev || []), { in: '', out: '' }])}>Add shift</button>
                                                        <button type="button" disabled={busy} onClick={saveDay}>Save changes</button>
                                                        <p>Times are Pacific. Leave Out blank only for a shift still in progress.</p>
                                                    </>
                                                )}
                                                {editMessage && <p className="timeclock-notice" role="status">{editMessage}</p>}
                                                {editError && <p className="timeclock-error" role="alert">{editError}</p>}
                                            </div>
                                        )}
                                    </>
                                )}
                                {!!state.entries?.length && (
                                    <>
                                        <h2>Mileage and expenses this pay period</h2>
                                        <ul>
                                            {state.entries.map(x => (
                                                <li key={x.id}>
                                                    {x.date}: {x.kind === 'miles'
                                                        ? `${x.miles} miles \u2014 ${x.purpose}`
                                                        : `$${(x.amount ?? 0).toFixed(2)} \u2014 ${x.items} (${x.vendor})${x.receipt ? ' \u2014 receipt attached' : ''}`}
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                                <h2>Time Entries during this Pay Period</h2>
                                <ul>
                                    {state.recent.map(p => (
                                        <li key={p.time}>
                                            {p.type === 'in' ? 'Clocked in' : 'Clocked out'} — {toPacific(p)}
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                        <button className="timeclock-secondary" onClick={() => logout()}>Log Out</button>
                    </>
                )}
                {notice && <p className="timeclock-notice" role="status">{notice}</p>}
                {error && <p className="timeclock-error" role="alert">{error}</p>}
            </div>
        </div>
    );
}

export default TimeClock;
